/**
 * Copyright (c) 2023, CYBRA Corp. All rights reserved.
 */
import {Component, OnInit, ViewChild, ElementRef, ChangeDetectorRef} from '@angular/core';
import {ActivatedRoute} from "@angular/router";

import {EmGroup} from "../../models/em-group";
import {GroupType} from "../../models/group-type";
import {InstanceData} from "../../models/instance-data";
import {ObjectCategory} from '../../models/object-category';
import {ObjectDefinition} from "../../models/object-definition";
import {ObjectInstance} from "../../models/object-instance";

import {GroupService} from "../../services/group.service";
import {ObjectService} from "../../services/object.service";

import {AddToGroupDialogComponent} from "../add-to-group-dialog.component";
import {ConfirmDeleteDialogComponent} from "../confirm-delete-dialog.component";
import {ManageGroupsDialogComponent} from "../manage-groups-dialog.component";
import {RemoveFromGroupDialogComponent} from "../remove-from-group-dialog.component";
import {ShowGroupDialogComponent} from "../show-group-dialog.component";

import {EntityInstanceDialogComponent} from './dialogs/entity-instance-dialog.component';
import {EntityTypeDialogComponent} from "./dialogs/entity-type-dialog.component";
import { INFRA_DUMMY_FUNCTION, RESULTS_PER_PAGE } from '../../app-constants';
import { PagedResults } from 'app/models/paged-results';
import { NgxSpinnerService } from 'ngx-spinner';
import { FunctionParameterDataUi } from './dialogs/general-function-dialog.component';
import { ERROR_DIALOG_TITLE, SimpleSuccessErrorDialogComponent } from '../simple-success-error-dialog-box/simple-success-error-dialog-box.component';
import { FilterOption, FilterType, REMOVE_FILTER } from '../../models/filter-option';


@Component({
  templateUrl: './devices.component.html',
  styleUrls: ['../../../assets/css/infrastructure.css']
})
export class DevicesComponent implements OnInit {

  @ViewChild('confirmDeleteDialog')
  mConfirmDeleteDialog: ConfirmDeleteDialogComponent;

  @ViewChild('showGroupDialog')
  mShowGroupDialog: ShowGroupDialogComponent;

  @ViewChild('manageGroupsDialog')
  mManageGroupsDialog: ManageGroupsDialogComponent;

  @ViewChild('addToGroupDialog')
  mAddToGroupDialogComponent: AddToGroupDialogComponent;

  @ViewChild('removeFromGroupDialog')
  mRemoveFromGroupDialogComponent: RemoveFromGroupDialogComponent;

  @ViewChild('entityTypeDialog')
  mEntityTypeDialogComponent: EntityTypeDialogComponent;

  @ViewChild('entityInstanceDialog')
  mEntityInstanceDialogComponent: EntityInstanceDialogComponent;

  @ViewChild('fileTypeUpload')
  mTypeFileUploadInput: ElementRef;

  @ViewChild('fileInstanceUpload')
  mInstanceFileUploadInput: ElementRef;

  /** Reference to the SimpleSuccessErrorDialog used to display error */
@ViewChild('successErrorDialog') 
simpleSuccessErrorDialogComponent: SimpleSuccessErrorDialogComponent;

  mDeviceObjects: DeviceObjectUI[] = [];
  mDeviceInstances: DeviceInstanceUI[] = [];

  mDeleteFlag = 0; //1 = Devices, 2 = Tag Instances


  //Entity Type Dialog Variables
  mEntity: ObjectDefinition;
  mEntityName: string;
  mEntityType = GroupType.OBJECT;
  mEntityTypeName = "Device";

 /* 
   * Filter Variables
   */
  /** Filter Options available to the Encoding Scheme Types */
  filterOptionsForTypes: FilterOption[] = [];
  /** Selected Filter Option for the Encoding Scheme Types */
  filterOptionForTypes: FilterOption = REMOVE_FILTER;
  /** Filter options available to the Encoding Scheme Instances */
  filterOptionsForInstances: FilterOption[] = [];
  /** Selected Filter Option for the Encoding Scheme Instances */
  filterOptionForInstances: FilterOption = REMOVE_FILTER;
  /** Active filters for the Device Instances (Name + Type), AND'd together */
  activeFilterOptionsForInstances: FilterOption[] = [];

  //Entity Instance Dialog Variables
  mInstance: ObjectInstance;
  mInstanceData: InstanceData[];
  mInstanceName: string;
  mInstanceEntityType: ObjectDefinition;
  mObjectCategory = ObjectCategory.DEVICE;
  mInstanceType = GroupType.OBJECTINSTANCE;
  mInstanceTypeName = "Device";
  mAvailableEntityTypes: ObjectDefinition[];

 /*
   * Paginator Variables for both Types and Instances
   */
  /** Total # of "Encoding Scheme" Types known to the system. (Used to figure out # of pages) */
  totalNumberOfTypes = 0;
  /** Total # of "Encoding Scheme" Instances known to the system. (Used to figure out he # of pages) */
  totalNumberOfInstances = 0;
  /** Current Page being displayed for the "Encoding Scheme" Types */
  currentPageForTypes = 1;
  /** Current Page being displayed for the "Encoding Scheme" Instances */
  currentPageForInstances = 1;

  // List for Dropdown of Results Per Page value
  resultsPerPageValues = RESULTS_PER_PAGE;
  selectedPageSizeValueForTypes: number = this.resultsPerPageValues[0];
  selectedPageSizeValueForInstances: number = this.resultsPerPageValues[0];

  // Variables for the dropdown menu 
  functionList = [];
  selectedFunction = [];
  showContextMenu: boolean = false;
  contextMenuPosition = { x: 0, y: 0 };

  // Variable for General Confirm/Cancel Dialog Box
  showGeneralDialog: boolean = false;
  functionParameterData: FunctionParameterDataUi[] = [];



  constructor(
    private mRoute: ActivatedRoute,
    private mObjectService: ObjectService,
    private mGroupService: GroupService,
    private cdr: ChangeDetectorRef,
    private spinnerService: NgxSpinnerService
  ) { }



  ngOnInit(): void {
  
    this.refreshFilterOptionsForTypes();
    this.refreshFilterOptionsForInstances();
    this.selectedPageSizeValueForTypes = this.resultsPerPageValues[0];
    this.selectedPageSizeValueForInstances = this.resultsPerPageValues[0];

    this.mRoute.data.subscribe(
      (data:
        {
          devices: PagedResults<ObjectDefinition>,
          deviceInstances: PagedResults<ObjectInstance>
        }
      ) => {
        this.updatePagedTypes(data.devices);
        this.updatePagedInstances(data.deviceInstances);
      }
    )
  }

  // Check if this function is being used 
  onTypePageChange(pageNumber?: number):void {
    if (pageNumber == null || pageNumber == undefined || pageNumber < 1) {
      return;
    }

    if(this.currentPageForTypes == pageNumber) {
      return;
    }

    this.currentPageForTypes = pageNumber;
    this.refreshTypes();
  }

  private async refreshTypes(): Promise<void> {
    this.spinnerService.show();

    let pagedData: PagedResults<ObjectDefinition>;
    try{
      pagedData = await this.mObjectService
        .getAllObjectsForCategoryPaging(ObjectCategory.DEVICE, (this.currentPageForTypes - 1) * this.selectedPageSizeValueForTypes, this.selectedPageSizeValueForTypes, "ASC", this.filterOptionForTypes.apiValue);
    } catch (error) {
      this.spinnerService.hide();
      this.handleError(error);
      return;
    }

    this.updatePagedTypes(pagedData);
    this.spinnerService.hide();

  }
  
  private updatePagedTypes(pagedData: PagedResults<ObjectDefinition>): void {
    const uiObjects: DeviceObjectUI[] = [];
    if (pagedData.data != null) {
      pagedData.data.forEach(device => {
        const ui = new DeviceObjectUI();
        ui.mDevice = device;
        ui.mSelected = false;
        uiObjects.push(ui);
      });
    }
    this.mDeviceObjects = uiObjects;
    this.totalNumberOfTypes = pagedData.size;
  }

  onInstancePageChange(pageNumber ?: number): void {
    if (pageNumber == null || pageNumber == undefined || pageNumber < 1) {
      return;
    }

    if (this.currentPageForInstances == pageNumber) {
      return;
    }

    this.currentPageForInstances = pageNumber;
    this.refreshInstances();

  }

  private async refreshInstances(): Promise<void> {
    this.spinnerService.show();

    let pagedData: PagedResults<ObjectInstance> ;
    try {
      pagedData = await this.mObjectService.getAllObjectInstancesForCategoryPagingWithFilters(ObjectCategory.DEVICE, (this.currentPageForInstances - 1) * this.selectedPageSizeValueForInstances, this.selectedPageSizeValueForInstances, "ASC", this.activeFilterOptionsForInstances);
    } catch (error) {
      this.spinnerService.hide();
      this.handleError(error);
      return;
    }

    this.updatePagedInstances(pagedData);
    this.spinnerService.hide();
  }

  private updatePagedInstances(pagedData: PagedResults<ObjectInstance>): void {
    const uiObjects: DeviceInstanceUI[] = [];
    if (pagedData.data != null) {
      pagedData.data.forEach(deviceInstance => {
        const ui = new DeviceInstanceUI();
        ui.mDeviceInstance = deviceInstance;
        ui.mSelected = false;
        uiObjects.push(ui);
      })
    }
    this.mDeviceInstances = uiObjects;
    this.totalNumberOfInstances = pagedData.size;
  }

  private refreshFilterOptionsForTypes(): void {
    this.filterOptionsForTypes = [
      REMOVE_FILTER,
      new FilterOption({ column: "name", label: "Name", type: FilterType.STRING }),
    ];
  }

  private refreshFilterOptionsForInstances(): void {
    this.filterOptionsForInstances = [
      REMOVE_FILTER,
      new FilterOption({ column: "name", label: "Name", type: FilterType.STRING }),
      new FilterOption({ column: "object.name", label: "Type", type: FilterType.STRING }),
    ];
  }

  manageResultPerPageClickedForTypes(value: number): void{
    this.selectedPageSizeValueForTypes = value;
    this.currentPageForTypes = 1;
    this.refreshTypes();
  }

  manageResultPerPageClickedForInstances(value: number): void {
    this.selectedPageSizeValueForInstances = value;
    this.currentPageForInstances = 1;
    this.refreshInstances();
  }



  isSingleDeviceObjectSelected(): boolean {
    if (this.mDeviceObjects != null && this.mDeviceObjects.length > 0) {
      let oneSelected = false;
      for (const deviceObject of this.mDeviceObjects) {
        if (deviceObject.mSelected === true) {
          if (oneSelected) {
            return false;
          } else {
            oneSelected = true;
          }
        }
      }
      return oneSelected;
    } else {
      return false;
    }
  }

  isAnyDeviceObjectsSelected(): boolean {
    return this.mDeviceObjects.some(deviceObject => deviceObject.mSelected);
  }

  isAllDeviceObjectsSelected(): boolean {
    return this.mDeviceObjects !== null && this.mDeviceObjects.every(deviceObject => deviceObject.mSelected);
  }

  setAllDeviceObjectsSelected(event: any): void {
    this.mDeviceObjects.forEach(deviceObject => deviceObject.mSelected = event.target.checked);
  }


  isSingleDeviceInstanceSelected(): boolean {
    if (this.mDeviceInstances != null && this.mDeviceInstances.length > 0) {
      let oneSelected = false;
      for (const deviceInstance of this.mDeviceInstances) {
        if (deviceInstance.mSelected === true) {
          if (oneSelected) {
            return false;
          } else {
            oneSelected = true;
          }
        }
      }
      return oneSelected;
    } else {
      return false;
    }
  }

  isAnyDeviceInstancesSelected(): boolean {
    return this.mDeviceInstances.some(deviceInstance => deviceInstance.mSelected);
  }

  isAllDeviceInstancesSelected(): boolean {
    return this.mDeviceInstances !== null && this.mDeviceInstances.every(deviceInstance => deviceInstance.mSelected);
  }

  setAllDeviceInstancesSelected(event: any): void {
    this.mDeviceInstances.forEach(deviceInstance => deviceInstance.mSelected = event.target.checked);
  }


  getSelectedDeviceObjects(): ObjectDefinition[] {
    const deviceObjects: ObjectDefinition[] = [];
    this.mDeviceObjects.filter(ui => ui.mSelected).forEach(ui => deviceObjects.push(ui.mDevice));
    return deviceObjects;
  }

  getSelectedDeviceObjectIds(): number[] {
    const ids: number[] = [];
    this.mDeviceObjects.filter(ui => ui.mSelected).forEach(ui => ids.push(ui.mDevice.id));
    return ids;
  }

  getSelectedDeviceObjectNames(): string[] {
    const names: string[] = [];
    this.mDeviceObjects.filter(ui => ui.mSelected).forEach(ui => names.push(ui.mDevice.name));
    return names;
  }





  getSelectedDeviceInstances(): ObjectInstance[] {
    const deviceInstances: ObjectInstance[] = [];
    this.mDeviceInstances.filter(ui => ui.mSelected).forEach(ui => deviceInstances.push(ui.mDeviceInstance));
    return deviceInstances;
  }

  getSelectedDeviceInstanceIds(): number[] {
    const ids: number[] = [];
    this.mDeviceInstances.filter(ui => ui.mSelected).forEach(ui => ids.push(ui.mDeviceInstance.id));
    return ids;
  }

  getSelectedDeviceInstanceNames(): string[] {
    const names: string[] = [];
    this.mDeviceInstances.filter(ui => ui.mSelected).forEach(ui => names.push(ui.mDeviceInstance.name));
    return names;
  }




  manageDeviceObjectGroupsClicked(): void {
    this.mManageGroupsDialog.show(GroupType.OBJECT);
  }

  manageDeviceInstanceGroupsClicked(): void {
    this.mManageGroupsDialog.show(GroupType.OBJECTINSTANCE);
  }

  addSelectedDeviceObjectsToGroupClicked(): void {
    if (this.isAnyDeviceObjectsSelected()) {
      this.mAddToGroupDialogComponent.show(GroupType.OBJECT, this.getSelectedDeviceObjectNames());
    }
  }

  addSelectedDeviceInstancesToGroupClicked(): void {
    if (this.isAnyDeviceInstancesSelected()) {
      this.mAddToGroupDialogComponent.show(GroupType.OBJECTINSTANCE, this.getSelectedDeviceInstanceNames());
    }
  }

  addSelectedToGroup(group: EmGroup): void {
    switch (group.type) {
      case GroupType.OBJECT: {
        this
          .mGroupService
          .addItemsToGroup(group.id, this.getSelectedDeviceObjectIds())
          .then(
            success => {
              this.refreshTypes();
            },
            error => this.handleError(error)
          );
        break;
      }
      case GroupType.OBJECTINSTANCE: {
        this
          .mGroupService
          .addItemsToGroup(group.id, this.getSelectedDeviceInstanceIds())
          .then(
            success => {
              this.refreshInstances();
            },
            error => this.handleError(error)
          );
        break;
      }
    }
  }





  removeSelectedDeviceObjectsFromGroupClicked(): void {
    if (this.isAnyDeviceObjectsSelected()) {
      const groupNames: string[] = [];
      const groups: EmGroup[] = [];
      const deviceObjectNames: string[] = [];
      this.getSelectedDeviceObjects().forEach(deviceObject => {
        deviceObjectNames.push(deviceObject.name);
        if (deviceObject.groups != null) {
          deviceObject.groups.forEach(group => {
            if (!groupNames.includes(group.name)) {
              groupNames.push(group.name);
              groups.push(group);
            }
          });
        }
      });
      this.mRemoveFromGroupDialogComponent.show(groups, deviceObjectNames);
    }
  }

  removeSelectedDeviceInstancesFromGroupClicked(): void {
    if (this.isAnyDeviceObjectsSelected()) {
      const groupNames: string[] = [];
      const groups: EmGroup[] = [];
      const deviceInstanceNames: string[] = [];
      this.getSelectedDeviceInstances().forEach(deviceInstance => {
        deviceInstanceNames.push(deviceInstance.name);
        if (deviceInstance.groups != null) {
          deviceInstance.groups.forEach(group => {
            if (!groupNames.includes(group.name)) {
              groupNames.push(group.name);
              groups.push(group);
            }
          });
        }
      });
      this.mRemoveFromGroupDialogComponent.show(groups, deviceInstanceNames);
    }
  }

  removeSelectedFromGroup(group: EmGroup): void {
    switch (group.type) {
      case GroupType.OBJECT: {
        this
          .mGroupService
          .removeItemsFromGroup(group.id, this.getSelectedDeviceObjectIds())
          .then(
            success => {
              this.refreshTypes();
            },
            error => this.handleError(error)
          );
        break;
      }
      case GroupType.OBJECTINSTANCE: {
        this
          .mGroupService
          .removeItemsFromGroup(group.id, this.getSelectedDeviceInstanceIds())
          .then(
            success => {
              this.refreshTypes();
            },
            error => this.handleError(error)
          );
        break;
      }
    }
  }


  showGroup(group: EmGroup): void {
    switch (group.type) {
      case GroupType.OBJECT: {
        this
          .mGroupService
          .getAllObjectsInGroup(group.id)
          .then(
            success => {
              const itemNames: string[] = [];
              success.forEach(member => itemNames.push(member.name));
              this.mShowGroupDialog.show(group.name, itemNames);
            },
            error => this.handleError(error)
          );
        break;
      }
      case GroupType.OBJECTINSTANCE: {
        this
          .mGroupService
          .getAllObjectInstancesInGroup(group.id)
          .then(
            success => {
              const itemNames: string[] = [];
              success.forEach(member => itemNames.push(member.name));
              this.mShowGroupDialog.show(group.name, itemNames);
            },
            error => this.handleError(error)
          );
        break;
      }
    }
  }


  addDeviceClicked(): void {
    const newEntity = new ObjectDefinition();
    newEntity.category = ObjectCategory.DEVICE;

    this.mEntity = newEntity
    this.mEntityName = null;
    this.mEntityTypeDialogComponent.mParents = this.mAvailableEntityTypes;
    this.mEntityTypeDialogComponent.entityParent = null;
    this.mEntityTypeDialogComponent.showDialog();
  }

  editSelectedDeviceClicked(): void {
    if (this.isSingleDeviceObjectSelected()) {
      this.editDevice(this.getSelectedDeviceObjects()[0]);
    }
  }

  duplicateSelectedDeviceClicked(): void {
    if (this.isSingleDeviceObjectSelected()) {
      this
        .mObjectService
        .getObjectForId(this.getSelectedDeviceObjects()[0].id)
        .then(
          response => {
            response.id = null;
            if (response.properties != null) {
              response.properties.forEach(p => p.id = null);
            }
            if (response.functions != null) {
              response.functions.forEach(f => f.id = null);
            }

            this.mEntity = response;
            this.mEntityName = null;
            this.mEntityTypeDialogComponent.showDialog();
          },
          error => this.handleError(error)
        );
    }
  }

  deleteSelectedDevicesClicked(): void {
    if (this.isAnyDeviceObjectsSelected()) {
      this.mDeleteFlag = 1; //1 = Devices
      this.mConfirmDeleteDialog.show("device type", this.getSelectedDeviceObjectNames());
    }
  }

  editDevice(device: ObjectDefinition): void {
    this
      .mObjectService
      .getObjectForId(device.id)
      .then(
        response => {
          this.mEntity = response;
          this.mEntityName = response.name;
          this.mEntityTypeDialogComponent.entityParent = response.parent;
          this.mEntityTypeDialogComponent.mParents = this.mAvailableEntityTypes;

          if (response.parent != null) {
            this.mEntityTypeDialogComponent.entityParent = this.mAvailableEntityTypes.find(p => p.id == response.parent.id);
          }

          this.mEntityTypeDialogComponent.showDialog();
        },
        error => this.handleError(error)
      );
  }

  createOrUpdateEntityType(): void {
    if (this.mEntity.id == null) {
      //ID = null, this is a new Device... Create it
      this
        .mObjectService
        .createObject(this.mEntity)
        .then(
          response => {
            this.refreshTypes();
            this.mEntityTypeDialogComponent.hideDialog();
          },
          error => this.handleError(error)
        );
    } else {
      //ID <> null, this is an existing Device... Update it
      this
        .mObjectService
        .updateObject(this.mEntity)
        .then(
          response => {
            this.refreshTypes();
            this.mEntityTypeDialogComponent.hideDialog();
          },
          error => this.handleError(error)
        );
    }
  }


  addDeviceInstanceClicked(): void {
    //Create a new Instance to be modeled in the Entity Instance Dialog
    this.mInstance = new ObjectInstance();
    this.mInstanceName = null;

    //Refresh the Availabe Types, Reload the Instance Data and Show the Dialog to start editing
    this.mEntityInstanceDialogComponent.setOriginalInstanceData([]);
    this.refreshTypesReloadDataAndShowDialog();
  }

  editSelectedDeviceInstanceClicked(): void {
    if (this.isSingleDeviceInstanceSelected()) {
      this.editDeviceInstance(this.getSelectedDeviceInstances()[0]);
    }
  }

  duplicateSelectedDeviceInstanceClicked(): void {
    if (this.isSingleDeviceInstanceSelected()) {
      this
      .mObjectService
      .getObjectInstanceForId(this.getSelectedDeviceInstances()[0].id)
      .then(
        response => {
          //Set the Instance to be modeled in the Entity Instance Dialog to the Instance returned...
          this.mInstance = response;

          //Set the Instance Name value so that it gets properly displayed in the Dialog Header
          this.mInstanceName = response.name;

          //Reset the IDs of the Instance and all the Instance Data so that it gets treated as new on the Server
          this.mInstance.id = null;
          this.mInstance.uuid = null;
          if (this.mInstance.data != null) {
            this.mInstance.data.forEach(d => d.id = null);
          }

          //Refresh the Availabe Types, Reload the Instance Data and Show the Dialog to start editing
          this.refreshTypesReloadDataAndShowDialog();
        },
        error => this.handleError(error)
      );
    }
  }

  deleteSelectedDeviceInstancesClicked(): void {
    if (this.isAnyDeviceInstancesSelected()) {
      this.mDeleteFlag = 2; //2 = Device Instances
      this.mConfirmDeleteDialog.show("device", this.getSelectedDeviceInstanceNames());
    }
  }

  editDeviceInstance(deviceInstance: ObjectInstance): void {
    this
      .mObjectService
      .getObjectInstanceForId(deviceInstance.id)
      .then(
        response => {
          //Set the Instance being modeled in the Entity Instance Dialog to the Instance that was returned
          this.mInstance = response;

          //Set the Instance Name which is displayed in the Dialog Header
          this.mInstanceName = response.name;

          // Set the original Instance Data being displayed by the EntityInstanceDialogComponent
          this.mEntityInstanceDialogComponent.setOriginalInstanceData(this.mInstance.data);

          //Refresh the Availabe Types, Reload the Instance Data and Show the Dialog to start editing
          this.refreshTypesReloadDataAndShowDialog();
        },
        error => this.handleError(error)
      );
  }

  async refreshTypesReloadDataAndShowDialog(): Promise<void> {
    //Create a List of the available Devices that the Instance can be a type of
    this.mAvailableEntityTypes = await this.mObjectService.getAllDeviceObjects();

    //Find/Set the Entity Type the Instance is the type of
    this.mInstanceEntityType = this.mInstance.object == null? null : this.mAvailableEntityTypes.find(entityType => entityType.id == this.mInstance.object.id);
    if (this.mInstanceEntityType != null) {
      this.mInstanceEntityType.functions = this.mInstance.object.functions;
      this.mInstanceEntityType.properties = this.mInstance.object.properties;
    }

    // Need to detect the changes here so that the "mInstanceEntityType" reference update gets passed to the EntityInstanceDialogComponent being shown below
    this.cdr.detectChanges();

    //Refresh the Instance Data, loading the existing values in for the Instance being edited
    this.mEntityInstanceDialogComponent.refreshData();

    //Show the Entity Instance Dialog
    this.mEntityInstanceDialogComponent.showDialog();
  }

  createOrUpdateEntityInstance(): void {
    console.log("createOrUpdateEntityInstance");
    //Set the values to the Device Instance to be created
    this.mInstance.object = this.mInstanceEntityType;

    if (this.mInstance.id == null) {
      //ID is null, Device Instance does not exist!  Create it...
      this
        .mObjectService
        .createObjectInstance(this.mInstance)
        .then(
          response => {
            this.refreshInstances();
            this.mEntityInstanceDialogComponent.hideDialog();
          },
          error => this.handleError(error)
        );
    } else {
      //ID is not null, Device Instance exists!  Update it...
      this
        .mObjectService
        .updateObjectInstance(this.mInstance)
        .then(
          response => {
            this.refreshInstances();
            this.mEntityInstanceDialogComponent.hideDialog();
          },
          error => this.handleError(error)
        );
    }
  }

  selectedEntityTypeChanged(entityType: ObjectDefinition): void {
    this.mInstanceEntityType = entityType;
    if (this.mInstanceEntityType != null && this.mInstanceEntityType.properties == null) {
      this
        .mObjectService
        .getObjectForId(this.mInstanceEntityType.id)
        .then(
          foundEntityType => {
            this.mInstanceEntityType.functions = foundEntityType.functions;
            this.mInstanceEntityType.properties = foundEntityType.properties;
            this.mEntityInstanceDialogComponent.refreshData();
          },
          error => this.handleError(error)
        );
    } else {
      this.mEntityInstanceDialogComponent.refreshData();
    }
  }

  refreshInstanceData(): void {
    this
      .mObjectService
      .getObjectInstanceForId(this.mInstance.id)
      .then(
        response => {
          this.mInstance.data = response.data;
          this.mEntityInstanceDialogComponent.setOriginalInstanceData(response.data);
          this.mEntityInstanceDialogComponent.refreshData();
        },
        error => this.handleError(error)
      );
  }





  deleteConfirmed(): void {
    if (this.mDeleteFlag == 1) {  //1 = Device Types
      this
        .mObjectService
        .deleteObjects(this.getSelectedDeviceObjectIds())
        .then(
          response => {
            //alert("Device Type deleted successfully !!");
            this.refreshTypes();
          },
          error => this.handleError(error)
        );
    } else if (this.mDeleteFlag == 2) {   //2 = Device Instances
      this
        .mObjectService
        .deleteObjectInstances(this.getSelectedDeviceInstanceIds())
        .then(
          response => {
            this.refreshInstances();
          },
          error => this.handleError(error)
        );
    }
  }

  uploadTypeFileClicked(): void {
    this.mTypeFileUploadInput.nativeElement.click();
  }

  onTypeFileUploadChange(event: any): void {
    if (event.target.files.length === 1) {
      this.uploadTypes(event.target.files[0]);
    }
  }

  uploadTypes(file: any): void {
    const formData = new FormData();
    formData.append("file", file);

    this.mObjectService.uploadTypes(formData).then(
        response => {
          console.log('Success');
        },
        error => {
          console.error(error);
        }
    );
  }

  downloadTypes(): void {
    this.mObjectService.downloadTypes().then(
        response => {
           const blob = new Blob([response], { type: 'text/csv' });
           const url = window.URL.createObjectURL(blob);
           const a = document.createElement("a");

           document.body.appendChild(a);
//           a.style = 'diplay: none';
           a.href = url;
           a.download = 'devicetypes.csv';
           a.click();
           window.URL.revokeObjectURL(url);
        },
        error => {
          console.error(error);
        }
    );
  }

  uploadInstanceFileClicked(): void {
    this.mInstanceFileUploadInput.nativeElement.click();
  }

  onInstanceFileUploadChange(event: any): void {
    if (event.target.files.length === 1) {
      this.uploadInstances(event.target.files[0]);
    }
  }

  uploadInstances(file: any): void {
    const formData = new FormData();
    formData.append("file", file);

    this.mObjectService.uploadInstances(formData).then(
        response => {
          console.log('Success');
        },
        error => {
          console.error(error);
        }
    );
  }

  downloadDevices(): void {
    const selectedTypes = this.mDeviceObjects.filter(type => type.mSelected);
    if (selectedTypes.length == 1) {
      this.downloadDevicesForTypeId(selectedTypes[0].mDevice.id);
    }
  }

  private async downloadDevicesForTypeId(typeId: number): Promise<void> {
    try {
      //Call the Server to download the instances, obtain the response...
      const response = await this.mObjectService.downloadInstances(typeId);

      //Handle the Response by making it available to download...
      const blob = new Blob([response], { type: 'text/csv' })
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = 'device-instances.csv';
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      this.handleError(error);
    }
    return;
  }

  handleError(error: any): void {
    /* New handling (TJB 04/18/2023) */
    if (error?.error != null) {
      this.simpleSuccessErrorDialogComponent.showErrorDialog(ERROR_DIALOG_TITLE, error.error);
    } 
    
    /* Default back to old handling */
    else if (error != null) {
      const response: Response = error;
      alert(response.text());
    }
  }

   /**-------------------------------------------------------------------------------------------------------------------- */

   // Called by <app-filter-bar> whenever the active filter or its value changes.
   onTypesFilterChanged(option: FilterOption): void {
    this.filterOptionForTypes = option;
    this.currentPageForTypes = 1;
    this.refreshTypes();
  }

  // Called by <app-filter-bar> whenever the active filter or its value changes.
  onInstancesFilterChanged(option: FilterOption): void {
    this.filterOptionForInstances = option;
    this.currentPageForInstances = 1;
    this.refreshInstances();
  }

  /*  
*  Handling the Right click event only on Devices Instance Name Column
*/
  fetchFunctionList(event: MouseEvent, deviceInstance: ObjectInstance): void {
    // Stop the default handling of right click event
    event.preventDefault();
    this.showContextMenu = true;
    this.showGeneralDialog = false;

    // Set the x & y co-ordinates for the context menu to open up
    this.contextMenuPosition.x = event.clientX;
    this.contextMenuPosition.y = event.clientY;

    // Empty the function List if any element still present
    this.functionList = []

    // Send the request to get the Devices Instance details using the ID
    this
      .mObjectService
      .getObjectInstanceForId(deviceInstance.id)
      .then(
        response => {
          //Set the Instance being modeled in the drop-down to the Instance that was returned
          this.mInstance = response;

          // Create the function List for the context menu from the response received 
          try {
            if (response.data && Array.isArray(response.object.functions) && (response.object.functions).length > 0) {
              this.functionList = response.object.functions;

            } else {
              // Input a dummy

              this.functionList = INFRA_DUMMY_FUNCTION;
            }
          }
          catch (error) {
            this.handleError(error)
          }
        },
        error => this.handleError(error)
      );

  }

  onItemClicked(item: any) {
    // Handle the click in the dropdown menu item here 
    this.selectedFunction = item;

    if (item.id != -1) {
      // Display the Dialog Box for confirmation
      this.showGeneralDialog = true;
    }

    // Hide the Context Menu/Dropdown
    this.showContextMenu = false;

  }

}


class DeviceObjectUI {
  mSelected = false;
  mDevice: ObjectDefinition;
}

class DeviceInstanceUI {
  mSelected = false;
  mDeviceInstance: ObjectInstance;
}